import { Injectable, inject, InjectionToken } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { API_CONFIG, ApiConfig, DEFAULT_API_CONFIG } from '../base/api-config';
import { ENSURE_FRESH_CREDENTIALS } from '../base/http-context.tokens';
import {
  nextSignalrRetryDelay,
  createSignalrLogger,
  waitForInFlightHubStart,
  buildSignalrHttpOptions,
} from './signalr-reconnect.util';

export type SignalRConnectionStatus = 'connected' | 'reconnecting' | 'disconnected';

/** Server-sent DTO when a payment's status changes */
export interface PaymentStatusChangedDto {
  paymentId: string;
  status: string;
  invoiceId?: string | null;
  updatedAt: string;
  statusDisplay?: string | null;
  errorMessage?: string | null;
  customerId?: string | null;
  marinaId?: string | null;
}

/** Server-sent when a marina's invoices may have changed (sidebar counts, billing list). */
export interface InvoiceRosterChangedDto {
  marinaId: string;
}

/**
 * Server-sent when the collection outstanding on an invoice ended without a payment.
 *
 * A payment that succeeds or fails arrives as a payment status change. A cancelled one has no
 * payment to report, so a till waiting on a card would otherwise wait for an outcome that is never
 * coming.
 */
export interface InvoiceCollectionChangedDto {
  invoiceId: string;
  marinaId?: string | null;
  /** `Released` | `Retryable` | `Checking` */
  state: string;
  reason?: string | null;
  reasonCode?: string | null;
  paymentIntentStatus?: string | null;
  canRetry?: boolean;
  updatedAt: string;
}

/** Optional factory for access token (e.g. for mobile or when cookie is not sent on WebSocket). */
export const PAYMENTS_HUB_ACCESS_TOKEN_FACTORY = new InjectionToken<() => Promise<string | null>>(
  'PAYMENTS_HUB_ACCESS_TOKEN_FACTORY'
);

/**
 * Single app-scoped SignalR connection to the payments hub.
 * One instance per app (providedIn: 'root'); one HubConnection created on first connect() and reused.
 * Connect once when user is authenticated; do not recreate on navigation.
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentsHubService {
  private connection: HubConnection | null = null;
  /** Single-flight so concurrent connect()/subscribe() callers wait for Connected. */
  private connectPromise: Promise<void> | null = null;
  /**
   * Bumped on each connect/disconnect so an in-flight start from a prior login
   * is dropped quietly instead of coming back after logout.
   */
  private connectionEpoch = 0;
  /** False after disconnect() until the next connect() — suppresses automatic reconnect. */
  private enabled = false;
  private readonly enabled$ = new BehaviorSubject<boolean>(false);
  private readonly config: ApiConfig = inject(API_CONFIG, { optional: true }) ?? DEFAULT_API_CONFIG;
  /** Awaited before connecting — SignalR bypasses the HTTP interceptors entirely. */
  private readonly ensureFreshCredentials = inject(ENSURE_FRESH_CREDENTIALS, { optional: true });
  private readonly accessTokenFactory = inject(PAYMENTS_HUB_ACCESS_TOKEN_FACTORY, { optional: true });
  private subscribedPaymentIds = new Set<string>();
  private subscribedCustomerIds = new Set<string>();
  private subscribedMarinaIds = new Set<string>();
  /** Ref count so multiple features (e.g. POS + sidebar) can share one hub subscription per marina. */
  private marinaSubscriptionRefCounts = new Map<string, number>();
  private statusChangedHandlers: ((dto: PaymentStatusChangedDto) => void)[] = [];
  private invoiceRosterChangedHandlers: ((dto: InvoiceRosterChangedDto) => void)[] = [];
  private collectionChangedHandlers: ((dto: InvoiceCollectionChangedDto) => void)[] = [];

  private readonly connectionState$ = new BehaviorSubject<SignalRConnectionStatus>('disconnected');
  private readonly hadEverConnected$ = new BehaviorSubject<boolean>(false);
  /** True once a connect() attempt has finished (success or failure). Set when attempt settles, not when it starts — avoids overlay flash on refresh. */
  private readonly connectAttempted$ = new BehaviorSubject<boolean>(false);

  /** Current connection status. */
  readonly connectionStatus$: Observable<SignalRConnectionStatus> = this.connectionState$.asObservable();

  /**
   * True when we should show "connection lost" overlay:
   * - We were connected and then disconnected, OR
   * - A connect attempt has completed and we are still disconnected (initial connect failed).
   * Logout sets enabled=false so this stays false while the socket is being torn down.
   */
  readonly connectionLost$: Observable<boolean> = combineLatest([
    this.connectionState$,
    this.hadEverConnected$,
    this.connectAttempted$,
    this.enabled$,
  ]).pipe(
    map(
      ([status, hadConnected, attempted, enabled]) =>
        enabled && status === 'disconnected' && (hadConnected || attempted)
    )
  );

  private get hubUrl(): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    return `${base}/hubs/payments`;
  }

  async connect(): Promise<void> {
    this.setEnabled(true);
    if (this.isHubConnected()) return;
    if (this.connectPromise) {
      await this.connectPromise;
      if (this.isHubConnected() || !this.enabled) return;
    }

    this.connectPromise = this.startConnection();
    try {
      await this.connectPromise;
    } finally {
      this.connectPromise = null;
    }
  }

  private async startConnection(): Promise<void> {
    if (this.isHubConnected()) return;

    const epoch = this.connectionEpoch;

    // Refresh first if the access token is spent. SignalR negotiates with its own HTTP client, so
    // nothing else will do this for us, and negotiating with a lapsed cookie means a 401 per hub and
    // a "Connection lost" dialog for what is really just a routine token refresh.
    await this.ensureFreshCredentials?.();
    if (!this.enabled || epoch !== this.connectionEpoch) return;

    if (this.connection) {
      const state = this.connection.state;
      if (state === HubConnectionState.Connected || state === HubConnectionState.Connecting) {
        return;
      }
      try {
        await this.connection.stop();
      } catch {
        // ignore teardown errors
      }
      this.connection = null;
    }
    if (!this.enabled || epoch !== this.connectionEpoch) return;

    const hub: HubConnection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, buildSignalrHttpOptions(this.ensureFreshCredentials, this.accessTokenFactory))
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext): number | null =>
          nextSignalrRetryDelay(this.enabled && this.connection === hub, retryContext.previousRetryCount),
      })
      .configureLogging(createSignalrLogger(() => this.enabled))
      .build();

    hub.on('PaymentStatusChanged', (dto: PaymentStatusChangedDto) => {
      this.statusChangedHandlers.forEach((h) => h(dto));
    });

    hub.on('InvoiceRosterChanged', (dto: InvoiceRosterChangedDto) => {
      this.invoiceRosterChangedHandlers.forEach((h) => h(dto));
    });

    hub.on('InvoiceCollectionChanged', (dto: InvoiceCollectionChangedDto) => {
      this.collectionChangedHandlers.forEach((h) => h(dto));
    });

    hub.onreconnecting(() => {
      if (!this.enabled || this.connection !== hub) return;
      this.connectionState$.next('reconnecting');
      console.warn('[PaymentsHubService] Connection lost - reconnecting…');
    });

    hub.onreconnected(async () => {
      if (!this.enabled || this.connection !== hub) return;
      this.connectionState$.next('connected');
      this.hadEverConnected$.next(true);
      for (const id of this.subscribedPaymentIds) {
        try {
          await hub.invoke('SubscribeToPayment', id);
        } catch (e) {
          console.warn('[PaymentsHubService] Resubscribe failed for payment', id, e);
        }
      }
      for (const id of this.subscribedCustomerIds) {
        try {
          await hub.invoke('SubscribeToCustomer', id);
        } catch (e) {
          console.warn('[PaymentsHubService] Resubscribe failed for customer', id, e);
        }
      }
      for (const id of this.subscribedMarinaIds) {
        try {
          await hub.invoke('SubscribeToMarina', id);
        } catch (e) {
          console.warn('[PaymentsHubService] Resubscribe failed for marina', id, e);
        }
      }
    });

    hub.onclose((error?: Error) => {
      if (this.connection !== hub) return;
      this.connectionState$.next('disconnected');
      if (this.enabled && this.hadEverConnected$.getValue()) {
        console.warn('[PaymentsHubService] Connection closed. Reload the page to reconnect.', error?.message ?? error);
      }
    });

    this.connection = hub;

    try {
      await hub.start();
      if (!this.enabled || epoch !== this.connectionEpoch || this.connection !== hub) {
        // Logout / another connect superseded this start — drop the hub quietly.
        await hub.stop().catch(() => {});
        if (this.connection === hub) {
          this.connection = null;
        }
        return;
      }
      this.connectionState$.next('connected');
      this.hadEverConnected$.next(true);
      this.connectAttempted$.next(true); // attempt finished (success)
    } catch (err) {
      if (this.connection === hub) {
        this.connectionState$.next('disconnected');
        this.connection = null;
        this.connectAttempted$.next(true); // attempt finished (failure)
      }
      if (!this.enabled || epoch !== this.connectionEpoch) {
        return;
      }
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    this.setEnabled(false);
    this.connectionEpoch += 1;
    const inFlight = this.connectPromise;
    const hub = this.connection;
    this.connection = null;
    this.subscribedPaymentIds.clear();
    this.subscribedCustomerIds.clear();
    this.subscribedMarinaIds.clear();
    this.marinaSubscriptionRefCounts.clear();
    this.connectionState$.next('disconnected');
    this.connectAttempted$.next(false);
    this.hadEverConnected$.next(false);
    await waitForInFlightHubStart(inFlight);
    if (this.connectPromise === inFlight) {
      this.connectPromise = null;
    }
    if (hub) {
      try {
        await hub.stop();
      } catch {
        // ignore teardown errors — logout must not wait on a dying socket
      }
    }
  }

  private setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.enabled$.getValue() !== enabled) {
      this.enabled$.next(enabled);
    }
  }

  onPaymentStatusChanged(handler: (dto: PaymentStatusChangedDto) => void): () => void {
    this.statusChangedHandlers.push(handler);
    return () => {
      this.statusChangedHandlers = this.statusChangedHandlers.filter((h) => h !== handler);
    };
  }

  onInvoiceCollectionChanged(handler: (dto: InvoiceCollectionChangedDto) => void): () => void {
    this.collectionChangedHandlers.push(handler);
    return () => {
      this.collectionChangedHandlers = this.collectionChangedHandlers.filter((h) => h !== handler);
    };
  }

  onInvoiceRosterChanged(handler: (dto: InvoiceRosterChangedDto) => void): () => void {
    this.invoiceRosterChangedHandlers.push(handler);
    return () => {
      this.invoiceRosterChangedHandlers = this.invoiceRosterChangedHandlers.filter((h) => h !== handler);
    };
  }

  async subscribeToPayment(paymentId: string): Promise<void> {
    if (!this.enabled) return;
    await this.ensureConnected();
    await this.connection!.invoke('SubscribeToPayment', paymentId);
    this.subscribedPaymentIds.add(paymentId);
  }

  async unsubscribeFromPayment(paymentId: string): Promise<void> {
    if (this.isHubConnected()) {
      await this.connection!.invoke('UnsubscribeFromPayment', paymentId);
    }
    this.subscribedPaymentIds.delete(paymentId);
  }

  async subscribeToCustomer(customerId: string): Promise<void> {
    if (!this.enabled) return;
    await this.ensureConnected();
    await this.connection!.invoke('SubscribeToCustomer', customerId);
    this.subscribedCustomerIds.add(customerId);
  }

  async unsubscribeFromCustomer(customerId: string): Promise<void> {
    if (this.isHubConnected()) {
      await this.connection!.invoke('UnsubscribeFromCustomer', customerId);
    }
    this.subscribedCustomerIds.delete(customerId);
  }

  async subscribeToMarina(marinaId: string): Promise<void> {
    const prev = this.marinaSubscriptionRefCounts.get(marinaId) ?? 0;
    const next = prev + 1;
    this.marinaSubscriptionRefCounts.set(marinaId, next);
    if (next === 1) {
      if (!this.enabled) {
        this.marinaSubscriptionRefCounts.delete(marinaId);
        return;
      }
      try {
        await this.ensureConnected();
        await this.connection!.invoke('SubscribeToMarina', marinaId);
        this.subscribedMarinaIds.add(marinaId);
      } catch (e) {
        this.marinaSubscriptionRefCounts.delete(marinaId);
        throw e;
      }
    }
  }

  async unsubscribeFromMarina(marinaId: string): Promise<void> {
    const prev = this.marinaSubscriptionRefCounts.get(marinaId) ?? 0;
    if (prev === 0) {
      return;
    }
    if (prev === 1) {
      this.marinaSubscriptionRefCounts.delete(marinaId);
      if (this.isHubConnected()) {
        await this.connection!.invoke('UnsubscribeFromMarina', marinaId);
      }
      this.subscribedMarinaIds.delete(marinaId);
    } else {
      this.marinaSubscriptionRefCounts.set(marinaId, prev - 1);
    }
  }

  private async ensureConnected(): Promise<void> {
    if (this.isHubConnected()) return;
    if (!this.enabled) {
      throw new Error('Payments SignalR connection is stopped');
    }
    await this.connect();
    if (!this.isHubConnected()) {
      throw new Error('Payments SignalR connection is not Connected');
    }
  }

  private isHubConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  get isConnected(): boolean {
    return this.isHubConnected();
  }
}
